import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import ZoomSharingComponents from './ZoomSharingComponents';
import CloseIcon from '@mui/icons-material/Close';
import { useZoom } from '../ZoomProvider';
import { useConference } from '../../Dolby';
import useAsyncEffect from 'use-async-effect';
import { useMytaverseEvent } from '../../../../providers';
import { useStyles } from './styles';
import { VideoPlayer, VideoQuality } from '@zoom/videosdk';
import ZoomCameraSharing from './ZoomCameraSharing';
import { useMytaverse } from '../../../../../../providers/MytaverseProvider';

const ZoomScreenPreview: React.FC = () => {
  const { ueScreenMediaStreams, ueWebcamMediaStreams } = useConference();
  const { sendMessageToUnreal } = useMytaverse();
  const { currentEvent } = useMytaverseEvent();
  const {
    expandedScreenSharing,
    setExpandedScreenSharing,
    mediaStream,
    zoomUser,
    zmClient,
    expandedCameraSharing,
    setExpandedCameraSharing,
  } = useZoom();
  const classes = useStyles();

  const [canvasWidth, setCanvasWidth] = useState(0.9 * window.innerWidth);
  const [canvasHeight, setCanvasHeight] = useState(0.9 * window.innerHeight);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setCanvasWidth(0.9 * window.innerWidth);
      setCanvasHeight(0.9 * window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const previewName = useMemo(
    () =>
      ueScreenMediaStreams[0]?.owner.name ||
      ueWebcamMediaStreams[0]?.owner.name,
    [ueScreenMediaStreams, ueWebcamMediaStreams],
  );

  const isScreenSharing = useMemo(
    () => expandedScreenSharing && ueScreenMediaStreams?.length > 0,
    [expandedScreenSharing, ueScreenMediaStreams],
  );

  const isCameraSharing = useMemo(
    () => expandedCameraSharing && ueWebcamMediaStreams?.length > 0,
    [expandedCameraSharing, ueWebcamMediaStreams],
  );

  const showPreview = useMemo(
    () =>
      (expandedCameraSharing && ueWebcamMediaStreams?.length) ||
      (expandedScreenSharing && ueScreenMediaStreams?.length),
    [
      expandedCameraSharing,
      expandedScreenSharing,
      ueWebcamMediaStreams,
      ueScreenMediaStreams,
    ],
  );

  const handleClose = useCallback(async () => {
    sendMessageToUnreal({ action: 'STOP_FULLSCREEN_SHARING' });
    setExpandedScreenSharing(false);
    setExpandedCameraSharing(false);
    setIsLoading(false);
    setError(null);
    if (ueWebcamMediaStreams?.length && mediaStream) {
      const canvasPreview = document.getElementById('remote-user-view-canvas');

      try {
        const zoomUser = zmClient.getCurrentUserInfo();

        if (zoomUser?.userId === (ueWebcamMediaStreams[0] as any).zoomUserId) {
          await mediaStream.detachVideo(
            (ueWebcamMediaStreams[0] as any).zoomUserId,
          );
        }

        await mediaStream.stopRenderVideo(
          canvasPreview as HTMLCanvasElement,
          (ueWebcamMediaStreams[0] as any).zoomUserId,
        );
      } catch (e) {}
    }

    if (mediaStream) {
      try {
        mediaStream.stopShareView();
      } catch (e) {}
    }
  }, [
    mediaStream,
    ueScreenMediaStreams,
    sendMessageToUnreal,
    setExpandedScreenSharing,
    setExpandedCameraSharing,
  ]);

  useAsyncEffect(async () => {
    if (!mediaStream || !ueScreenMediaStreams?.[0] || !expandedScreenSharing)
      return;

    setIsLoading(true);
    setError(null);

    try {
      if (
        (ueScreenMediaStreams[0] as any).zoomUserId &&
        zoomUser?.userId !== (ueScreenMediaStreams[0] as any).zoomUserId
      ) {
        const element = document.getElementById(
          'share-view-canvas',
        ) as HTMLCanvasElement;

        await mediaStream.stopShareView();

        await mediaStream
          .startShareView(element, (ueScreenMediaStreams[0] as any).zoomUserId)
          .catch((e) => {
            mediaStream.stopShareView();
            setError('Failed to start screen sharing. Please try again');
            console.error(e);
          });
      }
    } finally {
      setIsLoading(false);
    }

    return () => {
      mediaStream.stopShareView();
    };
  }, [mediaStream, ueScreenMediaStreams, expandedScreenSharing, zoomUser]);

  useAsyncEffect(async () => {
    if (!mediaStream || !ueWebcamMediaStreams?.[0] || !expandedCameraSharing)
      return;

    const videoContainer = document.querySelector('video-player-container');

    if ((ueWebcamMediaStreams[0] as any).zoomUserId) {
      const userVideo = await mediaStream.attachVideo(
        (ueWebcamMediaStreams[0] as any).zoomUserId,
        VideoQuality.Video_1080P,
      );
      videoContainer?.appendChild(userVideo as VideoPlayer);
      return;
    }
  }, [
    mediaStream,
    ueWebcamMediaStreams,
    expandedCameraSharing,
    zoomUser,
    canvasWidth,
    canvasHeight,
    zmClient,
  ]);

  return (
    <>
      {(expandedScreenSharing || expandedCameraSharing) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        />
      )}
      <Box
        sx={
          expandedScreenSharing || expandedCameraSharing
            ? classes.root
            : classes.notExpandedRoot
        }
      >
        {(isCameraSharing || isScreenSharing) && (
          <Typography className="user-name" sx={classes.userName}>
            {previewName}
          </Typography>
        )}

        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            message={error}
          />
        )}

        <ZoomSharingComponents
          style={
            expandedScreenSharing
              ? (classes.zoomPreview as React.CSSProperties)
              : undefined
          }
        />

        {expandedCameraSharing && (
          <ZoomCameraSharing
            style={
              expandedCameraSharing
                ? (classes.zoomCameraPreview as React.CSSProperties)
                : undefined
            }
            canvasHeight={canvasHeight}
            canvasWidth={canvasWidth}
          />
        )}

        {!showPreview && (
          <img
            style={classes.fullScreenImage as React.CSSProperties}
            src={currentEvent?.backgroundImage?.url}
            alt="Background"
          />
        )}

        {(expandedScreenSharing || expandedCameraSharing) && (
          <Box sx={classes.closeButtonContainer}>
            <IconButton
              className="close-button"
              sx={classes.closeButton}
              onClick={handleClose}
            >
              <CloseIcon sx={classes.closeIcon} />
            </IconButton>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ZoomScreenPreview;
